import {
  createClient,
  DeleteOperation,
  deleteRequestFactory,
  FindOperation,
  getRequestFactory,
  postRequestFactory,
  putRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory
} from '@infuse/api/common';
import {
  AddChargingStations,
  AddLocalTokenGroups,
  Location,
  NewLocation,
  RemoveChargingStations,
  RemoveLocalTokenGroups,
  UpdatedLocation
} from './model';

// Note: Locations are called Pools in the backend, but let's stick with Location in the UI as much as possible.
//       OCPI Locations were already called just Locations, so we needed a new term and landed on Pool.
const client = createClient('/rest/operator-api/pools');

export const locationPermissions = {
  SEARCH: 'cpms.search-pools',
  GET: 'cpms.get-pool',
  CREATE: 'cpms.create-pool',
  UPDATE: 'cpms.update-pool',
  DELETE: 'cpms.delete-pool',
  ADD_CHARGING_STATIONS: 'cpms.add-charging-stations-to-pool',
  REMOVE_CHARGING_STATIONS: 'cpms.remove-charging-stations-from-pool',
  ADD_LOCAL_TOKEN_GROUPS: 'cpms.add-local-token-groups-to-pool',
  REMOVE_LOCAL_TOKEN_GROUPS: 'cpms.remove-local-token-groups-from-pool'
};

export const searchLocations: SearchOperation<Location> = searchRequestFactory<Location>(client, {
  pattern: '',
  permissionId: locationPermissions.SEARCH
});

export const getLocation: FindOperation<Location, string> = getRequestFactory<Location, string>(client, {
  permissionId: locationPermissions.GET
});

export const createLocation: SaveOperation<NewLocation> = postRequestFactory<NewLocation, Location>(client, {
  pattern: '',
  permissionId: locationPermissions.CREATE
});

export const createLocationReturnUrl: SaveOperation<NewLocation, string, never> = postRequestFactory<
  NewLocation,
  string,
  never
>(client, {
  pattern: '',
  permissionId: locationPermissions.CREATE,
  returnLocation: true
});

export const updateLocation: SaveOperation<UpdatedLocation> = putRequestFactory<UpdatedLocation, Location, string>(
  client,
  { pattern: '/{locationId}', permissionId: locationPermissions.UPDATE }
);

export const deleteLocation: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{locationId}',
  permissionId: locationPermissions.DELETE
});

// Actions

export const addChargingStationsToLocation: SaveOperation<AddChargingStations, string> = postRequestFactory<
  AddChargingStations,
  string
>(client, {
  permissionId: locationPermissions.ADD_CHARGING_STATIONS,
  pattern: '/{locationId}/add-charging-stations'
});

export const removeChargingStationsFromLocation: SaveOperation<RemoveChargingStations, string> = postRequestFactory<
  RemoveChargingStations,
  string
>(client, {
  permissionId: locationPermissions.REMOVE_CHARGING_STATIONS,
  pattern: '/{locationId}/remove-charging-stations'
});

export const addLocalTokenGroupsToLocation: SaveOperation<AddLocalTokenGroups, string> = postRequestFactory<
  AddLocalTokenGroups,
  string
>(client, {
  permissionId: locationPermissions.ADD_LOCAL_TOKEN_GROUPS,
  pattern: '/{locationId}/add-local-token-groups'
});

export const removeLocalTokenGroupsFromLocation: SaveOperation<RemoveLocalTokenGroups, string> = postRequestFactory<
  RemoveLocalTokenGroups,
  string
>(client, {
  permissionId: locationPermissions.REMOVE_LOCAL_TOKEN_GROUPS,
  pattern: '/{locationId}/remove-local-token-groups'
});
